import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { AuditEventDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { useRealtimeEvent } from "@/realtime/useRealtime";
import { Panel, SectionHeader, type Tone, toneColor } from "@/design/primitives";
import { clockTime } from "@/lib/format";

const TAG_TONE: [RegExp, Tone][] = [
  [/INCIDENT|FAILED|OVERRIDE/, "red"],
  [/PROPOSAL|RISK|WARN/, "amber"],
  [/PASSED|DONE|RESOLVED/, "green"],
];

function actionTag(action: string): { label: string; tone: Tone } {
  const label = action.replaceAll("_", " ").split(" ")[0] ?? action;
  for (const [re, tone] of TAG_TONE) if (re.test(action)) return { label, tone };
  return { label, tone: "cyan" };
}

/** Živý stream posledních událostí (audit) — realtime přes WS. */
export function ActivityStream() {
  const query = useQuery({
    queryKey: ["audit", "stream"],
    queryFn: () => api.audit({ limit: 9 }),
  });
  const [live, setLive] = useState<AuditEventDTO[]>([]);

  useEffect(() => {
    if (query.data) setLive(query.data.events);
  }, [query.data]);

  useRealtimeEvent((message) => {
    if (message.type === "audit.created") {
      setLive((prev) => [message.event, ...prev].slice(0, 9));
    }
  });

  return (
    <Panel className="p-4" data-testid="activity-stream">
      <SectionHeader
        label={cs.commandCenter.activityStream}
        right={<Link to="/audit">AUDIT →</Link>}
      />
      <div className="mt-3 flex flex-col gap-2">
        {live.length === 0 ? (
          <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.audit.empty}
          </div>
        ) : (
          live.map((e) => {
            const tag = actionTag(e.action);
            return (
              <div key={e.id} className="af-animate-fade break-words text-[12px] leading-relaxed">
                <span style={{ color: "var(--text-tertiary)" }}>[{clockTime(e.ts)}]</span>{" "}
                <span style={{ color: toneColor[tag.tone], letterSpacing: ".08em" }}>{tag.label}</span>{" "}
                <span style={{ color: "var(--text-secondary)" }}>
                  {e.projectName ? `${e.projectName}: ` : ""}
                  {e.summary}
                </span>
              </div>
            );
          })
        )}
      </div>
    </Panel>
  );
}
